import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Trash2, Eye, FileText, Calendar, Save, Loader2, Upload, X, CheckCircle, XCircle, Clock } from 'lucide-react';
import { API } from '@/App';
import RichTextEditor from '@/components/RichTextEditor';

const CATEGORIES = ['Contactologie', 'Chirurgie réfractive', 'Cornée', 'Rétine', 'Glaucome', 'Pédiatrie', 'Recherche', 'Cas clinique', 'Autre'];

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  approved: { label: 'Approuvé', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  rejected: { label: 'Refusé', color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
  published: { label: 'Publié', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: FileText },
};

const ArticlesManagementPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [authorFilter, setAuthorFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', abstract: '', content: '', pdf_url: '',
    authors: [], keywords: [], category: 'Contactologie', published: true,
  });
  const [authorInput, setAuthorInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');

  useEffect(() => { fetchArticles(); }, []);

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${API}/articles`, { params: { published_only: false } });
      setArticles(res.data);
    } catch { toast.error('Erreur lors du chargement des articles'); }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ title: '', abstract: '', content: '', pdf_url: '', authors: [], keywords: [], category: 'Contactologie', published: true });
    setEditingArticle(null);
    setAuthorInput('');
    setKeywordInput('');
  };

  const handleEdit = (a) => {
    setEditingArticle(a);
    setForm({
      title: a.title || '', abstract: a.abstract || '', content: a.content || '',
      pdf_url: a.pdf_url || '', authors: getAuthors(a), keywords: Array.isArray(a.keywords) ? a.keywords : [],
      category: a.category || 'Contactologie', published: a.published !== false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingArticle) {
        await axios.put(`${API}/articles/${editingArticle.id}`, form);
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/articles`, form);
        toast.success('Article créé');
      }
      setDialogOpen(false);
      resetForm();
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Supprimer l'article "${title}" ?`)) return;
    try {
      await axios.delete(`${API}/articles/${id}`);
      toast.success('Article supprimé');
      fetchArticles();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleModerate = async (articleId, action) => {
    try {
      await axios.post(`${API}/articles/${articleId}/moderate`, { action });
      toast.success(action === 'approve' ? 'Article approuvé et publié' : 'Article refusé');
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de modération');
    }
  };

  const getArticleStatus = (a) => {
    if (a.status === 'pending') return 'pending';
    if (a.status === 'rejected') return 'rejected';
    if (a.status === 'approved' || a.published) return 'published';
    return 'draft';
  };

  const handlePdfUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/document`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, pdf_url: res.data.url }));
      toast.success('Document uploadé');
    } catch { toast.error("Erreur d'upload du document"); }
  };

  const addTag = (type) => {
    const input = type === 'author' ? authorInput.trim() : keywordInput.trim();
    if (!input) return;
    const key = type === 'author' ? 'authors' : 'keywords';
    if (!form[key].includes(input)) {
      setForm(prev => ({ ...prev, [key]: [...prev[key], input] }));
    }
    type === 'author' ? setAuthorInput('') : setKeywordInput('');
  };

  const removeTag = (type, val) => {
    const key = type === 'author' ? 'authors' : 'keywords';
    setForm(prev => ({ ...prev, [key]: prev[key].filter(t => t !== val) }));
  };

  const getAuthors = (a) => {
    if (!a.authors) return [];
    if (typeof a.authors === 'string') return [a.authors];
    if (Array.isArray(a.authors)) return a.authors;
    return [];
  };

  const filtered = articles.filter(a => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const matchSearch = a.title?.toLowerCase().includes(q) ||
        getAuthors(a).join(' ').toLowerCase().includes(q) ||
        (a.author || '').toLowerCase().includes(q) ||
        (a.abstract || '').toLowerCase().includes(q);
      if (!matchSearch) return false;
    }
    if (statusFilter !== 'all' && getArticleStatus(a) !== statusFilter) return false;
    if (categoryFilter !== 'all' && a.category !== categoryFilter) return false;
    if (yearFilter !== 'all') {
      const y = a.year || (a.created_at ? new Date(a.created_at).getFullYear() : null);
      if (String(y || '') !== String(yearFilter)) return false;
    }
    if (authorFilter !== 'all') {
      const auth = getAuthors(a).join(' ') || (a.author || '');
      if (!auth.includes(authorFilter)) return false;
    }
    return true;
  });

  const yearOptions = Array.from(new Set(articles.map(a => a.year || (a.created_at ? new Date(a.created_at).getFullYear() : null)).filter(Boolean))).sort((a, b) => b - a);
  const categoryOptions = Array.from(new Set(articles.map(a => a.category).filter(Boolean))).sort();
  const authorOptions = Array.from(new Set(articles.flatMap(a => getAuthors(a).length > 0 ? getAuthors(a) : (a.author ? [a.author] : [])).filter(Boolean))).sort();

  const resetFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setCategoryFilter('all'); setYearFilter('all'); setAuthorFilter('all');
  };
  const activeFiltersCount = [searchTerm, statusFilter !== 'all', categoryFilter !== 'all', yearFilter !== 'all', authorFilter !== 'all'].filter(Boolean).length;

  const pendingCount = articles.filter(a => a.status === 'pending').length;

  if (loading) return (
    <AdminLayout>
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#35AFE4]" />
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-6xl mx-auto" data-testid="articles-management">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
              <FileText size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Articles & Publications</h1>
              <p className="text-xs text-gray-500">Gérez les articles scientifiques</p>
            </div>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="create-article-btn">
            <Plus size={16} className="mr-2" /> Nouvel Article
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <p className="text-xs text-gray-500 mb-1">Total</p>
            <p className="text-2xl font-bold text-gray-900">{articles.length}</p>
          </div>
          <div className={`rounded-2xl border p-5 cursor-pointer transition-all ${statusFilter === 'pending' ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-200' : 'bg-white border-gray-100 hover:border-amber-200'}`}
            onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')} data-testid="filter-pending">
            <p className="text-xs text-gray-500 mb-1">En attente</p>
            <p className={`text-2xl font-bold ${pendingCount > 0 ? 'text-amber-600' : 'text-gray-900'}`}>{pendingCount}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <p className="text-xs text-gray-500 mb-1">Publiés</p>
            <p className="text-2xl font-bold text-gray-900">{articles.filter(a => a.published).length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <p className="text-xs text-gray-500 mb-1">Vues totales</p>
            <p className="text-2xl font-bold text-gray-900">{articles.reduce((s, a) => s + (a.views || 0), 0)}</p>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-4">
          {[
            { id: 'all', label: 'Tous', count: articles.length },
            { id: 'pending', label: 'En attente', count: pendingCount },
            { id: 'published', label: 'Publiés', count: articles.filter(a => getArticleStatus(a) === 'published').length },
            { id: 'rejected', label: 'Refusés', count: articles.filter(a => a.status === 'rejected').length },
          ].map(t => (
            <button key={t.id} onClick={() => setStatusFilter(t.id)}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${statusFilter === t.id ? 'bg-white text-[#1e3a5f] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`tab-filter-${t.id}`}>
              {t.label} ({t.count})
            </button>
          ))}
        </div>

        {/* Search + Filters */}
        <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-6 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
              <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-orange-200 outline-none bg-white"
                placeholder="Rechercher par titre, auteur, résumé..." data-testid="search-articles" />
            </div>

            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white max-w-[200px]" data-testid="filter-category">
              <option value="all">Toutes catégories</option>
              {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white" data-testid="filter-year">
              <option value="all">Toutes années</option>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>

            <select value={authorFilter} onChange={e => setAuthorFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white max-w-[220px]" data-testid="filter-author">
              <option value="all">Tous auteurs</option>
              {authorOptions.map(a => <option key={a} value={a}>{a}</option>)}
            </select>

            {activeFiltersCount > 0 && (
              <button onClick={resetFilters}
                className="px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-xl transition-colors flex items-center gap-1"
                data-testid="reset-filters">
                <X size={13} /> Tout effacer ({activeFiltersCount})
              </button>
            )}

            <span className="ml-auto text-xs text-gray-400 font-semibold whitespace-nowrap">
              {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <FileText size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-400">Aucun article trouvé</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">Titre</th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">Catégorie</th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">Auteurs</th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">Date</th>
                  <th className="px-5 py-3 text-center text-xs font-bold text-gray-500 uppercase">PDF</th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase">Statut</th>
                  <th className="px-5 py-3 text-right text-xs font-bold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((a, i) => (
                  <tr key={a.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`article-row-${i}`}>
                    <td className="px-5 py-3.5">
                      <p className="font-semibold text-sm text-gray-900">{a.title}</p>
                      <p className="text-xs text-gray-500 line-clamp-1">{a.abstract}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-50 text-orange-700">{a.category}</span>
                    </td>
                    <td className="px-5 py-3.5 text-xs text-gray-600">{getAuthors(a).join(', ') || a.author || 'SFOALC'}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <Calendar size={12} /> {new Date(a.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      {a.pdf_url ? (
                        <a href={a.pdf_url.startsWith('/') ? `${API.replace('/api', '')}${a.pdf_url}` : a.pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#35AFE4] hover:text-[#1e3a5f]" data-testid={`article-pdf-link-${i}`}>
                          <Eye size={14} />
                        </a>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      {(() => {
                        const st = getArticleStatus(a);
                        const cfg = STATUS_CONFIG[st] || STATUS_CONFIG.draft;
                        const Icon = cfg.icon;
                        return (
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.color}`}>
                            <Icon size={12} /> {cfg.label}
                          </span>
                        );
                      })()}
                      {a.author_id && <p className="text-[10px] text-gray-400 mt-0.5">Soumis par un auteur</p>}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {a.status === 'pending' && (
                          <>
                            <button onClick={() => handleModerate(a.id, 'approve')}
                              className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-500 hover:text-emerald-700 transition-colors" title="Approuver et publier"
                              data-testid={`approve-article-${a.id}`}>
                              <CheckCircle size={16} />
                            </button>
                            <button onClick={() => handleModerate(a.id, 'reject')}
                              className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors" title="Refuser"
                              data-testid={`reject-article-${a.id}`}>
                              <XCircle size={16} />
                            </button>
                          </>
                        )}
                        <button onClick={() => handleEdit(a)} className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors" data-testid={`edit-article-${i}`}><Edit2 size={14} /></button>
                        <button onClick={() => handleDelete(a.id, a.title)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors" data-testid={`delete-article-${i}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingArticle ? "Modifier l'article" : 'Nouvel article'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Titre *</Label>
                <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required data-testid="article-title-input" />
              </div>
              <div>
                <Label>Catégorie *</Label>
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="article-category-select">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Résumé / Abstract *</Label>
                <RichTextEditor value={form.abstract} onChange={val => setForm(p => ({ ...p, abstract: val }))} placeholder="Résumé de l'article..." height="100px" />
              </div>
              <div>
                <Label>Contenu *</Label>
                <RichTextEditor value={form.content} onChange={val => setForm(p => ({ ...p, content: val }))} placeholder="Contenu complet de l'article..." height="250px" />
              </div>
              {/* Authors Tags */}
              <div>
                <Label>Auteurs</Label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {form.authors.map(a => (
                    <span key={a} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs rounded-full font-medium">
                      {a} <button type="button" onClick={() => removeTag('author', a)}><X size={10} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={authorInput} onChange={e => setAuthorInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag('author'); } }}
                    placeholder="Dr Nom..." className="flex-1" data-testid="article-author-input" />
                  <Button type="button" variant="outline" onClick={() => addTag('author')} className="text-xs">Ajouter</Button>
                </div>
              </div>
              {/* Keywords Tags */}
              <div>
                <Label>Mots-clés</Label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {form.keywords.map(k => (
                    <span key={k} className="inline-flex items-center gap-1 px-2.5 py-1 bg-violet-50 text-violet-700 text-xs rounded-full font-medium">
                      {k} <button type="button" onClick={() => removeTag('keyword', k)}><X size={10} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={keywordInput} onChange={e => setKeywordInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag('keyword'); } }}
                    placeholder="contactologie, kératocône..." className="flex-1" data-testid="article-keyword-input" />
                  <Button type="button" variant="outline" onClick={() => addTag('keyword')} className="text-xs">Ajouter</Button>
                </div>
              </div>
              <div>
                <Label>Document PDF</Label>
                <div className="flex items-center gap-3">
                  {form.pdf_url && <a href={form.pdf_url} target="_blank" rel="noreferrer" className="text-xs text-[#35AFE4] hover:underline truncate max-w-xs">{form.pdf_url}</a>}
                  <label className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-medium cursor-pointer transition-colors">
                    <Upload size={12} /> Uploader un PDF
                    <input type="file" accept=".pdf" className="hidden" onChange={e => { if (e.target.files[0]) handlePdfUpload(e.target.files[0]); }} />
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.published} onChange={e => setForm(p => ({ ...p, published: e.target.checked }))} id="pub-article" className="rounded" />
                <label htmlFor="pub-article" className="text-sm text-gray-700">Publier immédiatement</label>
              </div>
              <Button type="submit" disabled={saving} className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="submit-article-btn">
                {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                {editingArticle ? 'Mettre à jour' : "Créer l'article"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ArticlesManagementPage;
